import { useEffect, useState } from 'react';
import { categoriaDeLlamada, type CategoriaLlamada } from './snapshot-types.js';

/**
 * Tiny hash-based router for drill-down overlays.
 *
 * Routes:
 *   #/calls/:id                        → CallDetail
 *   #/calls-list/<disposition>         → CallsList
 *   #/emails-list?scope=me|team        → EmailsList
 *   #/whatsapp-list?scope=me|team      → WhatsAppList
 *   #/meetings-list/<bucket>           → MeetingsList   (held|noshow|to_be_held|all)
 *   #/reuniones-grabadas               → ReunionesGrabadas: las grabaciones de Fluxo para Meet, página propia
 *   #/reunion/<id>                     → la misma página con esa reunión abierta (el enlace de la nota de GHL)
 *   #/won-deals?scope=me|team          → DealsList
 *   #/opps-list/<stage>?scope=me|team  → OpportunitiesList
 *   #/seguimiento[/<vista>]            → la pestaña «Seguimiento» (solo donde el servidor la enciende),
 *                                        en una de sus vistas: secuencia (por defecto: ciclos, toques y
 *                                        días por persona, solo lectura) o enviados. Las de antes (hoy,
 *                                        recibidos, cifras y derivaciones, quitadas en el revamp del 6-oct;
 *                                        borradores y whatsapp, quitadas el 20-oct al aparcar la redacción
 *                                        del panel) llevan a #/seguimiento.
 */

/**
 * Las vistas de la pestaña «Seguimiento». 20-oct: la redacción del panel, aparcada (la escribe la skill),
 * y Joan: «¿podemos hacer el panel full visual y quitar lo que ya no sirve?». Quedan «Secuencia», la
 * primera y la de #/seguimiento, y «Enviados» (también lo que sale de hola@ fuera del panel).
 */
export const VISTAS_SEGUIMIENTO = ['secuencia', 'enviados'] as const;
export type VistaSeguimientoRuta = typeof VISTAS_SEGUIMIENTO[number];

/** Los cuatro estados de las reuniones (revamp del 6-oct), o todas. */
export type CuboReuniones = 'por_hacer' | 'hecha' | 'no_show' | 'propuesta' | 'all';

/**
 * El cubo de un enlace a la lista de reuniones. Los nombres de antes (held,
 * noshow, to_be_held) siguen llevando a su estado: un enlace guardado no se rompe.
 */
export function cuboDeReuniones(b: string): CuboReuniones {
  if (b === 'por_hacer' || b === 'hecha' || b === 'no_show' || b === 'propuesta') return b;
  if (b === 'held') return 'hecha';
  if (b === 'noshow') return 'no_show';
  if (b === 'to_be_held') return 'por_hacer';
  return 'all';
}

export type HashRoute =
  | { kind: 'snapshot' }
  /**
   * La pestaña «Seguimiento» de Fluxo Dashboard. Sin el modulo encendido se ve el panel.
   * `antigua`: se llego por una vista que ya no existe (o que nunca existio): la app cambia el
   * hash a #/seguimiento sin dejarla en el historial.
   */
  | { kind: 'seguimiento'; vista?: VistaSeguimientoRuta; antigua?: true }
  | { kind: 'clientes' }
  | { kind: 'catalogo' }
  | { kind: 'diagnostico'; id: string }
  | { kind: 'call'; id: string }
  /** A meeting recorded with Fluxo para Meet, open in the meetings page (the GHL note links here). */
  | { kind: 'reunion'; id: string }
  /** Every recording made with Fluxo para Meet, newest first, as a page (27-oct, Joan: «tipo Granola»). */
  | { kind: 'reuniones-grabadas' }
  | { kind: 'calls-list'; disposition: string; categoria?: CategoriaLlamada }
  | { kind: 'emails-list'; scope: 'me' | 'team'; filter?: 'all' | 'replied' }
  | { kind: 'whatsapp-list'; scope: 'me' | 'team'; filter?: 'all' | 'replied' }
  | { kind: 'meetings-list'; bucket: CuboReuniones }
  | { kind: 'won-deals'; scope: 'me' | 'team' }
  | { kind: 'opps-list'; stage: string; scope: 'me' | 'team' }
  | { kind: 'feedback'; chatId?: string }
  | { kind: 'johny'; chatId?: string }
  | { kind: 'onboarding' }
  /**
   * La vista antigua del alta, solo para las altas de antes del recorrido (E6). `diagnostico`: el
   * diagnóstico del que salió con el traspaso de antes, que la reabre en su resumen (ver Alta). Un
   * alta del recorrido (con `diagnosticoId`) no se abre aquí: se redirige a su cliente.
   */
  | { kind: 'alta'; slug?: string; diagnostico?: string };

function readScope(qs: URLSearchParams): 'me' | 'team' {
  return qs.get('scope') === 'team' ? 'team' : 'me';
}
function readFilter(qs: URLSearchParams): 'all' | 'replied' {
  return qs.get('filter') === 'replied' ? 'replied' : 'all';
}

const DIAGNOSTICO_ID = /^diag_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;
/** ¿Es un id de diagnóstico que la ruta `#/diagnostico/{id}` abre? */
export function esIdDiagnostico(id: unknown): id is string {
  return typeof id === 'string' && DIAGNOSTICO_ID.test(id);
}

export function parseHash(hash: string): HashRoute {
  // Split off query string portion (everything after `?`).
  const [pathPart, queryPart = ''] = hash.split('?');
  const qs = new URLSearchParams(queryPart);
  const diagnostico = /^#\/diagnostico\/(diag_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})$/.exec(pathPart);
  if (diagnostico) return { kind: 'diagnostico', id: diagnostico[1] };
  if (pathPart === '#/clientes') return { kind: 'clientes' };
  if (pathPart === '#/seguimiento') return { kind: 'seguimiento' };
  const seguimientoM = /^#\/seguimiento\/([a-z]+)$/.exec(pathPart);
  if (seguimientoM) {
    const vista = VISTAS_SEGUIMIENTO.find((v) => v === seguimientoM[1]);
    // Una vista quitada (borradores, whatsapp, hoy...) o que no existe lleva a #/seguimiento (la «Secuencia»).
    return vista ? { kind: 'seguimiento', vista } : { kind: 'seguimiento', antigua: true };
  }
  if (pathPart === '#/catalogo') return { kind: 'catalogo' };

  const oppsM = /^#\/opps-list\/([^/?]+)/.exec(pathPart);
  if (oppsM) return { kind: 'opps-list', stage: decodeURIComponent(oppsM[1]), scope: readScope(qs) };

  const meetM = /^#\/meetings-list\/([^/?]+)/.exec(pathPart);
  if (meetM) return { kind: 'meetings-list', bucket: cuboDeReuniones(decodeURIComponent(meetM[1])) };

  if (pathPart.startsWith('#/emails-list')) return { kind: 'emails-list', scope: readScope(qs), filter: readFilter(qs) };
  if (pathPart.startsWith('#/whatsapp-list')) return { kind: 'whatsapp-list', scope: readScope(qs), filter: readFilter(qs) };
  if (pathPart.startsWith('#/won-deals')) return { kind: 'won-deals', scope: readScope(qs) };

  const feedbackM = /^#\/feedback(?:\/([^/?]+))?/.exec(pathPart);
  if (feedbackM) return { kind: 'feedback', chatId: feedbackM[1] ? decodeURIComponent(feedbackM[1]) : undefined };

  // Se ACEPTAN las dos grafias (johny y johnny) porque el enlace viejo vive en la
  // Custom Page que un cliente tiene registrada en su GHL, y cambiarlo es un paso
  // manual. Se ESCRIBE siempre la nueva (ver navigate mas abajo): se acepta lo
  // antiguo, no se propaga.
  const johnyM = /^#\/johnn?y(?:\/([^/?]+))?/.exec(pathPart);
  if (johnyM) return { kind: 'johny', chatId: johnyM[1] ? decodeURIComponent(johnyM[1]) : undefined };

  if (pathPart.startsWith('#/onboarding')) return { kind: 'onboarding' };

  const altaM = /^#\/alta(?:\/([^/?]+))?/.exec(pathPart);
  if (altaM) {
    const slug = altaM[1] ? decodeURIComponent(altaM[1]) : undefined;
    const diagnostico = qs.get('diagnostico');
    return slug && diagnostico && DIAGNOSTICO_ID.test(diagnostico) ? { kind: 'alta', slug, diagnostico } : { kind: 'alta', slug };
  }

  const callsListM = /^#\/calls-list\/([^/?]+)/.exec(pathPart);
  if (callsListM) {
    const categoria = categoriaDeLlamada(qs.get('categoria'));
    if (qs.has('categoria') && !categoria) return { kind: 'snapshot' };
    return { kind: 'calls-list', disposition: decodeURIComponent(callsListM[1]), ...(categoria ? { categoria } : {}) };
  }

  const callM = /^#\/calls\/([^/?]+)/.exec(pathPart);
  if (callM) return { kind: 'call', id: callM[1] };

  if (pathPart === '#/reuniones-grabadas') return { kind: 'reuniones-grabadas' };

  // Its query (the `?volver=` of 27-oct, before the page had the list beside it) is ignored.
  const reunionM = /^#\/reunion\/([0-9a-f-]{36})$/i.exec(pathPart);
  if (reunionM) return { kind: 'reunion', id: reunionM[1].toLowerCase() };

  return { kind: 'snapshot' };
}

export function useHashRoute(): HashRoute {
  const [route, setRoute] = useState<HashRoute>(() => parseHash(typeof window !== 'undefined' ? window.location.hash : ''));
  useEffect(() => {
    const onChange = (): void => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

/** El hash de una ruta. El del dashboard (snapshot) es el vacío. */
export function hashDeRuta(route: HashRoute): string {
  switch (route.kind) {
    case 'seguimiento':
      return route.vista ? `#/seguimiento/${route.vista}` : '#/seguimiento';
    case 'clientes':
    case 'catalogo':
      return `#/${route.kind}`;
    case 'alta':
      return hashAlta(route.slug, route.diagnostico);
    case 'diagnostico':
      return `#/diagnostico/${encodeURIComponent(route.id)}`;
    case 'snapshot':
      return '';
    case 'call':
      return `#/calls/${route.id}`;
    case 'reunion':
      return `#/reunion/${route.id}`;
    case 'reuniones-grabadas':
      return '#/reuniones-grabadas';
    case 'calls-list':
      return `#/calls-list/${encodeURIComponent(route.disposition)}${route.categoria ? `?categoria=${route.categoria}` : ''}`;
    case 'emails-list':
      return `#/emails-list?scope=${route.scope}${route.filter && route.filter !== 'all' ? '&filter='+route.filter : ''}`;
    case 'whatsapp-list':
      return `#/whatsapp-list?scope=${route.scope}${route.filter && route.filter !== 'all' ? '&filter='+route.filter : ''}`;
    case 'meetings-list':
      return `#/meetings-list/${route.bucket}`;
    case 'won-deals':
      return `#/won-deals?scope=${route.scope}`;
    case 'opps-list':
      return `#/opps-list/${encodeURIComponent(route.stage)}?scope=${route.scope}`;
    case 'feedback':
      return route.chatId ? `#/feedback/${encodeURIComponent(route.chatId)}` : '#/feedback';
    case 'johny':
      return route.chatId ? `#/johnny/${encodeURIComponent(route.chatId)}` : '#/johnny';
    case 'onboarding':
      return '#/onboarding';
  }
}

export function navigate(route: HashRoute): void {
  window.location.hash = hashDeRuta(route);
}

/**
 * Cambia de pantalla SIN dejar la ruta de antes en el historial: "Atrás" no vuelve a una
 * dirección que redirige otra vez (E6: un alta del recorrido abre su cliente). Reemplazar
 * solo el hash es una navegación dentro del documento: avisa con hashchange y la app cambia
 * de pantalla, sin recargar. El dashboard (hash vacío) se navega como siempre: reemplazar
 * por una dirección vacía recargaría la página.
 */
export function redirigir(route: HashRoute): void {
  const hash = hashDeRuta(route);
  if (hash && typeof window.location.replace === 'function') window.location.replace(hash);
  else window.location.hash = hash;
}

/** `#/alta`, `#/alta/slug` o `#/alta/slug?diagnostico=id`. */
export function hashAlta(slug?: string, diagnostico?: string): string {
  return slug ? `#/alta/${encodeURIComponent(slug)}${diagnostico ? `?diagnostico=${encodeURIComponent(diagnostico)}` : ''}` : '#/alta';
}

/**
 * Cambia la ruta SIN avisar a la app: replaceState no dispara hashchange, así que la
 * pantalla no se vuelve a montar ni el historial gana un paso. Sirve para que recargar
 * abra lo que ya se ve (el alta recién creada, no un alta en blanco).
 */
export function reemplazarHash(hash: string): void {
  try {
    if (typeof window === 'undefined' || !window.location || typeof window.history?.replaceState !== 'function') return;
    if (window.location.hash !== hash) window.history.replaceState(window.history.state, '', hash);
  } catch { /* sin historial que tocar: la ruta se queda como estaba */ }
}
